import { Tabs, message } from 'antd'
import SingleLineField from '../forms/SingleLineField'
import FormModal from './FormModal'
import VideoUploadInput from '../forms/utils/VideoUploadInput'
import FormItem from '../forms/utils/FormItem'
import { Switch } from '../lists/listComponents/Switch'
import { useEffect, useState } from 'react'
import { cld } from '../../utils/cloudinary'
import { useMutation } from '@apollo/client'
import { CREATE_VIDEO } from '../../utils/api/video'

export interface EditorVideoModalValues {
  src?: string
  videoId?: string
  videoDbId?: string
  interscroller?: boolean
  type: VideoType
}

interface EditorVideoModalProps {
  open: boolean
  defaultValues?: Partial<EditorVideoModalValues>
  onOk: (values: EditorVideoModalValues) => void
  onCancel: () => void
}

export enum VideoType {
  CONTIMO = 'CONTIMO',
  YOUTUBE = 'YOUTUBE'
}

export default function EditorVideoModal({ onOk, defaultValues, open, ...props }: EditorVideoModalProps) {
  const [createVideo] = useMutation(CREATE_VIDEO)

  const editMode = defaultValues?.type !== undefined
  const hasExistingVideo = editMode && defaultValues?.videoId
  const initialTab = defaultValues?.type || VideoType.CONTIMO
  const [activeKey, setActiveKey] = useState<string>(initialTab)

  // Reset state when modal opens/closes or defaultValues change
  useEffect(() => {
    if (open) {
      setActiveKey(defaultValues?.type || VideoType.CONTIMO)
    }
  }, [open, defaultValues?.type])

  const formDefaultValues = editMode
    ? {
        src: defaultValues?.src || '',
        video: defaultValues?.videoId ? { videoId: defaultValues.videoId, isBunny: true } : undefined,
        interscroller: defaultValues?.interscroller || false,
      }
    : {
        src: '',
        interscroller: false,
      }

  const handleOnOk = async (values: any) => {
    if (activeKey === VideoType.CONTIMO) {
      if (values.video?.videoId && !values.video?.isBunny) {
        try {
          const res = await createVideo({
            variables: {
              record: {
                name: values.video.name,
                videoDownloadUrl: cld.video(values.video.videoId).toURL()
              }
            }
          })
          if (!res.data?.video?._id) {
            throw new Error('Could not save the video. Please try again.')
          }
          onOk({ videoId: res.data.video.videoId, videoDbId: res.data.video._id, interscroller: values.interscroller, type: activeKey })
        } catch (e: any) {
          message.error(e?.message || 'Could not save the video. Please try again.')
          throw e // keep the modal open so the user can retry
        }
      } else if (editMode && defaultValues?.videoId) {
        onOk({ videoId: defaultValues.videoId, interscroller: values.interscroller, type: activeKey })
      }
    } else {
      onOk({ ...values, type: activeKey })
    }
  }

  const title = editMode ? 'Edit Video' : 'Add Video'
  const okText = editMode ? 'Edit Video' : 'Add Video'

  return (
    <FormModal
      {...props}
      open={open}
      onOk={handleOnOk}
      title={title}
      okText={okText}
      defaultValues={formDefaultValues}
      skipDirtyValidation
    >
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        destroyInactiveTabPane
        items={[
          {
            key: VideoType.CONTIMO,
            label: 'Upload',
            children: (
              <>
                <FormItem label="Video" top>
                  <VideoUploadInput
                    required={!hasExistingVideo}
                    name="video"
                  />
                </FormItem>
                <FormItem label="Interscroller">
                  <Switch name="interscroller" />
                </FormItem>
              </>
            ),
          },
          {
            key: VideoType.YOUTUBE,
            label: 'YouTube',
            children: (
              <SingleLineField
                label="URL"
                top
                htmlfor="src"
                autoFocus
                required
              />
            ),
          },
        ]}
      />
    </FormModal>
  )
}
